import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config';
import '../styles/procurement-admin.css';

function newId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function passwordBytes(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  return unescape(encodeURIComponent(value)).length;
}

const emptyCreate = () => ({ name: '', mobile: '', password: '', confirmPassword: '' });

export default function StandaloneAggregators() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const pending = useRef(null);

  const request = useCallback(async (path, body) => {
    const response = await fetch(`${API_BASE}/procurement/v1${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        Authorization: localStorage.getItem('token'),
        'Content-Type': 'application/json',
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.message || 'Procurement request failed');
    return result;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request('/admin/standalone-aggregators');
      setRows(Array.isArray(result.standalone_aggregators) ? result.standalone_aggregators : []);
      setMessage('');
    } catch (error) {
      setMessage(error.message || 'Could not load Standalone Aggregators.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  function commandFor(action, entityId, data, retryScope = entityId) {
    const fingerprint = JSON.stringify({ action, retryScope, data });
    if (!pending.current || pending.current.fingerprint !== fingerprint) {
      pending.current = {
        fingerprint,
        command: { command_id: newId(), entity_id: entityId, action, data },
      };
    }
    return pending.current.command;
  }

  async function submitCommand(action, entityId, data, successMessage, retryScope = entityId) {
    setBusy(true);
    setMessage('');
    try {
      await request('/commands', commandFor(action, entityId, data, retryScope));
      pending.current = null;
      try {
        await load();
        setMessage(successMessage);
      } catch {
        setMessage(`${successMessage} The list could not be refreshed; please refresh this page.`);
      }
      return true;
    } catch (error) {
      setMessage(error.message || 'Procurement command failed.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function createAggregator(event) {
    event.preventDefault();
    const name = createForm.name.trim();
    const mobile = createForm.mobile.trim();
    if (!name || !mobile || !createForm.password) {
      setMessage('Name, mobile, and password are required.');
      return;
    }
    if (createForm.password !== createForm.confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    const bytes = passwordBytes(createForm.password);
    if (Array.from(createForm.password).length < 4 || bytes > 72) {
      setMessage('Password must be at least 4 characters and at most 72 UTF-8 bytes.');
      return;
    }
    const created = await submitCommand('membership.create', newId(), {
      role: 'AGGREGATOR',
      name,
      login: mobile,
      password: createForm.password,
    }, 'Standalone Aggregator created.', 'create-standalone-aggregator');
    if (created) {
      setCreateForm(emptyCreate());
      setCreateOpen(false);
    }
  }

  async function changeMembership(row) {
    const active = !row.membership_active;
    const confirmation = active
      ? `Activate ${row.name}'s Aggregator membership in the current FPO?`
      : `Deactivate ${row.name}'s Aggregator membership in the current FPO? This does not disable the shared login or memberships in other FPOs.`;
    if (!window.confirm(confirmation)) return;
    await submitCommand(
      `standalone.aggregator.membership.${active ? 'activate' : 'deactivate'}`,
      row.membership_id,
      {},
      `Aggregator membership ${active ? 'activated' : 'deactivated'} in this FPO.`,
    );
  }

  async function confirmReset(event) {
    event.preventDefault();
    if (!resetTarget || !resetPassword) {
      setMessage('Enter a new password.');
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
    const bytes = passwordBytes(resetPassword);
    if (Array.from(resetPassword).length < 4 || bytes > 72) {
      setMessage('Password must be at least 4 characters and at most 72 UTF-8 bytes.');
      return;
    }
    const target = resetTarget;
    const reset = await submitCommand(
      'standalone.aggregator.password.reset',
      target.membership_id,
      { password: resetPassword },
      'Aggregator password reset.',
    );
    if (reset) {
      setResetTarget(null);
      setResetPassword('');
      setResetConfirmPassword('');
    }
  }

  return <section className="proc-admin proc-admin--identity-management" aria-label="Standalone Aggregators">
    <header className="proc-admin-identity-header">
      <div>
        <h2>Standalone Aggregators</h2>
        <p>Manage standalone Procurement Aggregator memberships for the current FPO.</p>
      </div>
      <div className="proc-admin-identity-header-actions">
        <button className="proc-admin-identity-button proc-admin-identity-button--primary" type="button" disabled={busy} onClick={() => { setCreateOpen(true); setMessage(''); }}>+ Create Aggregator</button>
        <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={busy || loading} onClick={() => load().catch(() => {})}>Refresh</button>
      </div>
    </header>
    <p className="proc-admin-identity-guidance">Membership status and availability apply to this FPO. Changing a membership does not disable the shared login or memberships in other FPOs.</p>
    {message && <p className="proc-admin-identity-message" role="status">{message}</p>}

    {createOpen && <form className="proc-admin-identity-card proc-admin-identity-form" aria-label="Create Standalone Aggregator" onSubmit={createAggregator}>
      <h3>Create Standalone Aggregator</h3>
      <label>Name *<input aria-label="Aggregator name" required value={createForm.name} onChange={event => setCreateForm(form => ({ ...form, name: event.target.value }))} /></label>
      <label>Mobile *<input aria-label="Aggregator mobile" type="tel" inputMode="tel" required value={createForm.mobile} onChange={event => setCreateForm(form => ({ ...form, mobile: event.target.value }))} /></label>
      <label>Password *<input aria-label="Aggregator password" type="password" autoComplete="new-password" required value={createForm.password} onChange={event => setCreateForm(form => ({ ...form, password: event.target.value }))} /></label>
      <label>Confirm password *<input aria-label="Confirm aggregator password" type="password" autoComplete="new-password" required value={createForm.confirmPassword} onChange={event => setCreateForm(form => ({ ...form, confirmPassword: event.target.value }))} /></label>
      <p>Password must be at least 4 characters and at most 72 UTF-8 bytes. The backend validates the mobile number.</p>
      <div className="proc-admin-identity-form-actions"><button className="proc-admin-identity-button proc-admin-identity-button--primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Create'}</button>
        <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={busy} onClick={() => { setCreateOpen(false); setCreateForm(emptyCreate()); setMessage(''); }}>Cancel</button></div>
    </form>}

    {resetTarget && <form className="proc-admin-identity-card proc-admin-identity-form" aria-label="Reset Aggregator password" onSubmit={confirmReset}>
      <h3>Reset password for {resetTarget.name}</h3>
      <label>New password *<input aria-label="New Aggregator password" type="password" autoComplete="new-password" required value={resetPassword} onChange={event => setResetPassword(event.target.value)} /></label>
      <label>Confirm new password *<input aria-label="Confirm new Aggregator password" type="password" autoComplete="new-password" required value={resetConfirmPassword} onChange={event => setResetConfirmPassword(event.target.value)} /></label>
      <p>Password must be at least 4 characters and at most 72 UTF-8 bytes.</p>
      <div className="proc-admin-identity-form-actions"><button className="proc-admin-identity-button proc-admin-identity-button--primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Reset password'}</button>
        <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={busy} onClick={() => { setResetTarget(null); setResetPassword(''); setResetConfirmPassword(''); setMessage(''); }}>Cancel</button></div>
    </form>}

    <section className="proc-admin-identity-card" aria-label="Standalone Aggregator list">
      <h3>Aggregator memberships</h3>
      {loading ? <p className="proc-admin-identity-state">Loading Standalone Aggregators…</p> : rows.length === 0
        ? <p className="proc-admin-identity-state">No standalone Aggregator memberships in this FPO.</p>
        : <div className="proc-admin-identity-table-scroll" role="region" aria-label="Standalone Aggregator table" tabIndex={0}><table className="proc-admin-identity-table">
          <thead><tr><th>Name</th><th>Login</th><th>Login type</th><th>Membership</th><th>Availability</th><th>Actions</th></tr></thead>
          <tbody>{rows.map(row => {
            const active = Boolean(row.membership_active);
            const canReset = row.can_reset_password === true;
            return <tr key={row.membership_id}>
              <td>{row.name}</td>
              <td>{row.login || '—'}</td>
              <td>{row.login_type === 'MOBILE' ? 'Mobile' : 'Legacy login'}</td>
              <td><span className={`proc-admin-identity-badge ${active ? 'proc-admin-identity-badge--active' : 'proc-admin-identity-badge--inactive'}`}>{active ? 'Active' : 'Inactive'}</span></td>
              <td><span className={`proc-admin-identity-badge ${row.effective_active ? 'proc-admin-identity-badge--available' : 'proc-admin-identity-badge--inactive'}`}>{row.effective_active ? 'Available' : 'Unavailable'}</span></td>
              <td><div className="proc-admin-identity-row-actions">
                <button className={`proc-admin-identity-button ${active ? 'proc-admin-identity-button--danger' : 'proc-admin-identity-button--secondary'}`} type="button" disabled={busy} onClick={() => changeMembership(row)}>{active ? 'Deactivate' : 'Activate'}</button>
                <button className="proc-admin-identity-button proc-admin-identity-button--secondary" type="button" disabled={busy || !canReset} title={!canReset ? (row.reset_block_reason || 'Password reset is unavailable.') : undefined}
                  onClick={() => { setResetTarget(row); setResetPassword(''); setResetConfirmPassword(''); setMessage(''); }}>Reset password</button>
                {!canReset && <span className="proc-admin-identity-action-note">{row.reset_block_reason || 'Password reset is unavailable.'}</span>}
              </div></td>
            </tr>;
          })}</tbody>
        </table></div>}
    </section>
  </section>;
}
